/**
 * Releasing a courier from a courier job (W4-COURIER-RELEASE, D378, ADR-0051) — the pure decision.
 *
 * A courier can be stuck on a job: five wrong handover PINs lock the handover (`HANDOVER_PIN_LOCKED`), a courier stops
 * answering, an app fails mid-job. The courier keeps the courier-level exclusion (`courier_active_jobs`), so they are
 * never offered another ride or courier job until the job ends. An OPERATOR releases them, with a stable reason code.
 *
 * What a release does depends on where the goods are:
 *  - **before the pickup** (`accepted`, `en_route_pickup`, `at_pickup` — custody is still the sender's): the engagement
 *    takes the lifecycle's own `→ unassigned` edge, the Send returns to the dispatch pool (`courier_assigned →
 *    confirmed`), the job searches again (`SEARCHING`) and the released courier is never re-offered THIS job
 *    (`REDISPATCHED`). A source that went away meanwhile (a food order cancelled, a Send cancelled or failed) is not
 *    re-offered: the job ends `CANCELLED` (`SOURCE_CANCELLED`);
 *  - **after the pickup** (`picked_up`, `en_route_drop`, `at_drop`, `returning` — the goods are with the courier): the job
 *    cannot be re-offered. It ends `FAILED` (`NOT_DELIVERED`): the Send `failed`, a food order `cancelled` by ops, the
 *    guest's cash not collected; the partner-account charge voids through SEAM B.
 *
 * In every outcome the courier's active assignment ends and they become offerable again. The reason code is the
 * operator's (a closed vocabulary, never free text); an optional note is bounded and required only for
 * `OPERATIONAL_OTHER`. `PIN_LOCKED` is accepted only for a job whose handover is actually locked, so the audit trail
 * cannot claim a lock that never happened. Every decision carries `COURIER_RELEASE_POLICY_VERSION`.
 *
 * Neutral: no country, currency, locale or unit literal.
 */
import { err, ok } from '@vello/core';
/** Bumped when the release rules change, so every recorded release stays explainable. */
export const COURIER_RELEASE_POLICY_VERSION = 'courier-release-v1';
/** Why an operator released a courier — the closed vocabulary the Admin offers and the audit stores. */
export const COURIER_RELEASE_REASONS = [
    /** The handover PIN locked after the attempt limit (only valid when the job's handover is locked). */
    'PIN_LOCKED',
    /** Support cannot reach the courier, or the courier stopped progressing. */
    'COURIER_UNREACHABLE',
    /** The courier asked to be released (a vehicle problem, an emergency). */
    'COURIER_REQUESTED_RELEASE',
    /** The courier's app cannot continue the job. */
    'APP_FAILURE',
    /** The recipient cannot be found or reached at the drop-off. */
    'RECIPIENT_UNAVAILABLE',
    /** Anything else — a note is required. */
    'OPERATIONAL_OTHER',
    /** D856 (R2): the sender asked to cancel AFTER the pickup (a Send only; refused before the pickup, where the sender's own cancel applies). */
    'SENDER_CANCELLED',
];
export function isCourierReleaseReason(v) {
    return typeof v === 'string' && COURIER_RELEASE_REASONS.includes(v);
}
/** The longest operator note a release keeps (characters, after trimming). */
export const COURIER_RELEASE_NOTE_MAX_LENGTH = 500;
export const COURIER_RELEASE_OUTCOMES = ['REDISPATCHED', 'NOT_DELIVERED', 'SOURCE_CANCELLED'];
/** The stable decision reason stamped on the job for each outcome. */
export const COURIER_RELEASE_DECISION = {
    REDISPATCHED: 'COURIER_RELEASED_REDISPATCH',
    NOT_DELIVERED: 'COURIER_RELEASED_NOT_DELIVERED',
    SOURCE_CANCELLED: 'COURIER_RELEASED_SOURCE_CANCELLED',
};
/** The job's dispatch status after each outcome. */
export const COURIER_RELEASE_DISPATCH_STATUS = {
    REDISPATCHED: 'SEARCHING',
    NOT_DELIVERED: 'FAILED',
    SOURCE_CANCELLED: 'CANCELLED',
};
/** Engagement states with the goods still at the sender (the lifecycle's `→ unassigned` edges). */
export const BEFORE_PICKUP_ENGAGEMENTS = ['accepted', 'en_route_pickup', 'at_pickup'];
/** Engagement states with the goods on the courier. */
export const AFTER_PICKUP_ENGAGEMENTS = ['picked_up', 'en_route_drop', 'at_drop', 'returning'];
/**
 * D858 (owner decision 2026-10-31): a `PIN_LOCKED` release says WHOSE error it was - the recipient's (the code the recipient was given, or the recipient cannot be reached) or the
 * courier's (the courier mistyped or lost the code). Only `PIN_LOCKED` carries it; it decides who pays (`afterPickupCauseOf`).
 */
export const COURIER_RELEASE_ATTRIBUTIONS = ['RECIPIENT', 'COURIER'];
export function isCourierReleaseAttribution(v) {
    return typeof v === 'string' && COURIER_RELEASE_ATTRIBUTIONS.includes(v);
}
/** Validate what the operator sent: a known reason, and a bounded note (required for `OPERATIONAL_OTHER`). */
export function validateCourierReleaseRequest(input) {
    if (!isCourierReleaseReason(input.reasonCode))
        return err('COURIER_RELEASE_REASON_INVALID');
    // D858: `PIN_LOCKED` must say whose error it was; no other reason takes an attribution (the reason already says it).
    const hasAttribution = input.attribution !== undefined && input.attribution !== null && input.attribution !== '';
    if (input.reasonCode === 'PIN_LOCKED' && !hasAttribution)
        return err('COURIER_RELEASE_ATTRIBUTION_REQUIRED');
    if (hasAttribution && (input.reasonCode !== 'PIN_LOCKED' || !isCourierReleaseAttribution(input.attribution)))
        return err('COURIER_RELEASE_ATTRIBUTION_INVALID');
    if (input.note !== undefined && input.note !== null && typeof input.note !== 'string')
        return err('COURIER_RELEASE_NOTE_INVALID');
    const note = typeof input.note === 'string' ? input.note.trim() : '';
    if (note.length > COURIER_RELEASE_NOTE_MAX_LENGTH)
        return err('COURIER_RELEASE_NOTE_INVALID');
    if (input.reasonCode === 'OPERATIONAL_OTHER' && note === '')
        return err('COURIER_RELEASE_NOTE_REQUIRED');
    return ok({ reasonCode: input.reasonCode, ...(note !== '' ? { note } : {}), ...(hasAttribution && isCourierReleaseAttribution(input.attribution) ? { attribution: input.attribution } : {}) });
}
/**
 * Decide a release over the job's current state. A job with no courier (`SEARCHING`, `ASSISTED`) has nobody to release;
 * a job that already ended (`CANCELLED`, `FAILED`, a delivered one, a courier already released) is a clear refusal.
 */
export function planCourierRelease(input) {
    if (input.dispatchStatus === 'SEARCHING' || input.dispatchStatus === 'ASSISTED')
        return err('COURIER_JOB_NOT_ASSIGNED');
    if (input.dispatchStatus !== 'ASSIGNED' || input.engagementState === undefined)
        return err('COURIER_JOB_ALREADY_ENDED');
    const before = BEFORE_PICKUP_ENGAGEMENTS.includes(input.engagementState);
    const after = AFTER_PICKUP_ENGAGEMENTS.includes(input.engagementState);
    if (!before && !after)
        return err('COURIER_JOB_ALREADY_ENDED');
    if (input.reasonCode === 'PIN_LOCKED' && !input.pinLocked)
        return err('COURIER_RELEASE_REASON_NOT_APPLICABLE');
    // D856: the sender cancelling is a reason for a job that ENDS after the pickup; before it, the sender's own cancel (or a redispatch) applies.
    if (input.reasonCode === 'SENDER_CANCELLED' && !after)
        return err('COURIER_RELEASE_REASON_NOT_APPLICABLE');
    const outcome = after ? 'NOT_DELIVERED' : input.sourceGone ? 'SOURCE_CANCELLED' : 'REDISPATCHED';
    return ok({
        outcome,
        decisionReasonCode: COURIER_RELEASE_DECISION[outcome],
        dispatchStatus: COURIER_RELEASE_DISPATCH_STATUS[outcome],
        phase: after ? 'after_pickup' : 'before_pickup',
        policyVersion: COURIER_RELEASE_POLICY_VERSION,
    });
}
/** D856 (R2) + D858: why a Send that ended after its pickup ended - decides who pays (`send.afterPickupPolicy`, `@vello/config`). The same tokens as config's `SEND_AFTER_PICKUP_CAUSES`. */
export const SEND_AFTER_PICKUP_CAUSE_CODES = ['SENDER_CANCELS', 'RECIPIENT_UNREACHABLE', 'COURIER_FAULT', 'PLATFORM_FAULT'];
/**
 * D856 (R2) + D858: the cause an operator's release names, for a Send released after its pickup. The sender cancelling is the sender's; a recipient who cannot be found is the delivery
 * being impossible; the courier giving up or not answering is the courier's; an app that fails is the PLATFORM's (D858: the sender is never charged, Vello pays the courier); a
 * `PIN_LOCKED` handover is the recipient's or the courier's as the OPERATOR says (`attribution`). Anything else (`OPERATIONAL_OTHER`) is treated as no one's fault of the sender's or
 * recipient's - the courier-fault rule, which by default charges nobody: a note is required for it.
 *
 * `undefined` for a `PIN_LOCKED` release with no attribution: the cause cannot be decided (the request is refused before; the Admin preview shows nothing until one is chosen).
 */
export function afterPickupCauseOf(reason, attribution) {
    switch (reason) {
        case 'SENDER_CANCELLED':
            return 'SENDER_CANCELS';
        case 'RECIPIENT_UNAVAILABLE':
            return 'RECIPIENT_UNREACHABLE';
        case 'PIN_LOCKED':
            return attribution === 'RECIPIENT' ? 'RECIPIENT_UNREACHABLE' : attribution === 'COURIER' ? 'COURIER_FAULT' : undefined;
        case 'APP_FAILURE':
            return 'PLATFORM_FAULT';
        case 'COURIER_UNREACHABLE':
        case 'COURIER_REQUESTED_RELEASE':
        case 'OPERATIONAL_OTHER':
            return 'COURIER_FAULT';
    }
}
/** The food order's cancellation reason for an in-transit release: the operator's code, namespaced and lower-case. */
export function foodReleaseReasonCode(reason) {
    return `courier_released_${reason.toLowerCase()}`;
}
//# sourceMappingURL=courier-release.js.map